import Service, { service } from '@ember/service';
import { dasherize } from '@ember/string';
import { isBlank, isEmpty } from '@ember/utils';
import { task, all } from 'ember-concurrency';
import WieSettings from 'eflex/constants/work-instructions/wie-settings';
import { waitFor } from '@ember/test-waiters';
import { removeObjects } from 'eflex/util/array-helpers';
import { intoArray, dpPluck, dpEq } from '@eflexsystems/ramda-helpers';
import AnimationEvents from 'eflex/constants/jem/instruction-animation-events';
import { without, map, any, uniqBy, reject, filter, chain, flow, prop, findLast, propEq, collectBy } from 'ramda';
import { compact, concatRight } from 'ramda-adjunct';
import { getJemConfig } from 'eflex/services/location-repo';
import { getWorkInstructionTriggerConfigs } from 'eflex/services/task-repo';
import { taskTypes } from 'eflex/constants/tasks/task-types';

import { query as emberDataQuery } from '@ember-data/legacy-compat/builders';

const COPYABLE_MODELS = new Set(['work-instruction-hardware-trigger-config', 'station-jem-configuration']);

const getMatchedBoltAnimations = (childStatus, animations) => {
  if (!animations) {
    return [];
  }

  const activeBoltNumber = childStatus?.activeBolt?.boltNumber;

  const currentBoltProcessData = childStatus?.groupedBoltProcessData.map(bolts => {
    const bolt = bolts.at(-1);
    if (bolt.isPassed || bolts.length === 1) {
      return bolt;
    } else {
      return bolts.at(-2);
    }
  });

  return intoArray(
    chain(boltData =>
      flow(animations, [
        intoArray(
          filter(propEq(`${boltData.boltNumber}`, 'bolt')),
          filter(animation => {
            switch (animation.event) {
              case AnimationEvents.START: {
                // show start animations for an active bolt that is started,
                // or bolts that have pass or fail data (meaning they were started at one time)
                return (
                  (boltData.boltNumber === activeBoltNumber && boltData.isStarted) ||
                  boltData.isPassed ||
                  boltData.isRejected
                );
              }

              case AnimationEvents.PASS: {
                return boltData.isPassed;
              }

              case AnimationEvents.REJECT: {
                return boltData.isRejected;
              }

              default: {
                return null;
              }
            }
          }),
        ),
        collectBy(prop('object')),
        // pass/reject animation supersedes start animation per bolt/object
        chain(objectAnimations => {
          const hasReject = any(prop('isPassOrReject'), objectAnimations);
          return reject(animation => animation.event === AnimationEvents.START && hasReject)(objectAnimations);
        }),
      ]),
    ),
    compact,
  )(currentBoltProcessData ?? []);
};

const getMatchedAllBoltAnimations = (childStatus, animations) => {
  const activeBolt = childStatus.activeBolt;

  const previousBolt = findLast(
    propEq(activeBolt?.boltNumber - 1, 'boltNumber'),
    childStatus.boltProcessData ?? [],
  );

  return flow(animations ?? [], [
    filter(propEq('all', 'bolt')),
    collectBy(prop('object')),
    intoArray(
      chain(objectAnimations => {
        const filteredAnimations = filter(animation => {
          switch (animation.event) {
            case AnimationEvents.START: {
              return activeBolt?.isStarted;
            }

            case AnimationEvents.PASS: {
              return previousBolt?.isPassed;
            }

            case AnimationEvents.REJECT: {
              return activeBolt?.isRejected;
            }

            default: {
              return null;
            }
          }
        }, objectAnimations);

        const hasReject = any(propEq(AnimationEvents.REJECT, 'event'), filteredAnimations);

        return reject(animation => animation.event === AnimationEvents.PASS && hasReject)(filteredAnimations);
      }),
      compact,
    ),
  ]);
};

const getMatchedAnimations = (childStatus, animations) => {

  return flow(animations ?? [], [
    collectBy(prop('object')),
    intoArray(
      chain(objectAnimations => {
        const filteredAnimations = filter(animation => {
          switch (animation.event) {
            case AnimationEvents.START: {
              return childStatus.isStarted;
            }

            case AnimationEvents.PASS: {
              return childStatus.isPassed;
            }

            case AnimationEvents.REJECT: {
              return childStatus.isRejected;
            }

            default: {
              return null;
            }
          }
        }, objectAnimations);

        const hasReject = any(propEq(AnimationEvents.REJECT, 'event'), filteredAnimations);

        return reject(animation => animation.event === AnimationEvents.PASS && hasReject)(filteredAnimations);
      }),
      compact,
    ),
  ]);
};

export const getAnimationsToRun = (childStatus, animations) => {
  const matched = [];

  if (!(childStatus != null && !isEmpty(animations))) {
    return matched;
  }

  if (childStatus.task.taskType == taskTypes.torque) {
    return flow(matched, [
      concatRight(getMatchedBoltAnimations(childStatus, animations)),
      concatRight(getMatchedAllBoltAnimations(childStatus, animations)),
      reject(prop('isDeleted')),
      uniqBy(animation => `${animation.object}${animation.bolt}${animation.event}`),
    ]);
  } else {
    return flow(matched, [
      concatRight(getMatchedAnimations(childStatus, animations)),
      reject(prop('isDeleted')),
      uniqBy(animation => `${animation.object}${animation.event}`),
    ]);
  }
};

const getContextsToCopy = (treeItem, selectedContext) => {
  let isSelectAll = true;
  if (Array.isArray(selectedContext)) {
    isSelectAll = selectedContext.length === 1 && selectedContext[0].allSelected;
  }

  if (treeItem.usesComponents) {
    return isSelectAll ? treeItem.component.options : selectedContext;
  } else {
    return isSelectAll ? treeItem.area.models : selectedContext;
  }
};

const getJemConfigs = (treeItem, sourceConfig, contextsToCopy) => {
  if (treeItem.type !== 'station') {
    return [];
  }

  return intoArray(
    map(context => getJemConfig(treeItem.jemConfigurations, context)),
    without([sourceConfig]),
    compact,
  )(contextsToCopy);
};

const getTriggerConfigs = (treeItem, sourceConfig, contextsToCopy, event) => {
  return intoArray(
    chain(context => getWorkInstructionTriggerConfigs(treeItem, context)),
    without([sourceConfig]),
    filter(dpEq(event, 'parentTrigger.event')),
  )(contextsToCopy);
};

export const copyToSelectedContexts = (treeItem, sourceConfig, event, selectedContext) => {
  if (sourceConfig == null || !COPYABLE_MODELS.has(sourceConfig.constructor.modelName)) {
    return;
  }

  const isJemConfig = sourceConfig.constructor.modelName === 'station-jem-configuration';

  const contextsToCopy = getContextsToCopy(treeItem, selectedContext);

  const destinationConfigs = isJemConfig
    ? getJemConfigs(treeItem, sourceConfig, contextsToCopy)
    : getTriggerConfigs(treeItem, sourceConfig, contextsToCopy, event);

  const { externalImageUrl } = sourceConfig;
  if (externalImageUrl != null) {
    destinationConfigs.forEach(destinationConfig => {
      destinationConfig.setExternalImageUrl(externalImageUrl, sourceConfig.page);
    });
  } else {
    const { workInstruction } = sourceConfig;
    destinationConfigs.forEach(destinationConfig => {
      destinationConfig.setWorkInstruction(workInstruction);
    });
  }

  const { animations } = sourceConfig;
  if (animations != null) {
    for (const config of destinationConfigs) {
      config.copyAnimations(animations);
    }
  }
};

export default class WorkInstructionRepoService extends Service {
  @service store;
  @service systemConfig;
  @service licensing;
  @service logRepo;
  @service taskRepo;
  @service workInstructionImageRepo;
  @service userRepo;
  @service intl;
  @service fileDownloader;
  @service currentUser;

  folders = this.store.peekAll('work-instruction-folder');
  workInstructions = this.store.peekAll('work-instruction');
  workInstructionIcons = this.store.peekAll('work-instruction-icon');

  get isApprovalActive() {
    return (
      this.systemConfig.config?.enableWieApprovals &&
      this.licensing.license.wieApprovalEnabled
    );
  }

  get nonDeletedWieFolders() {
    return this.folders.filter(item => !item.isDeleted);
  }

  get userFolderWieEditorTags() {
    return this.getUserFolderWieTags('wieEditorTags');
  }

  get userFolderWieApproverTags() {
    return this.getUserFolderWieTags('wieApproverTags');
  }

  get userFolderWieDeployerTags() {
    return this.getUserFolderWieTags('wieDeployerTags');
  }

  get systemFolderWieEditorTags() {
    return this.getSystemFolderWieTags('wieEditorTags');
  }

  get systemFolderWieApproverTags() {
    return this.getSystemFolderWieTags('wieApproverTags');
  }

  get systemFolderWieDeployerTags() {
    return this.getSystemFolderWieTags('wieDeployerTags');
  }

  get systemUserWieEditorTags() {
    return this.getSystemUserWieTags('wieEditorTags');
  }

  get systemUserWieApproverTags() {
    return this.getSystemUserWieTags('wieApproverTags');
  }

  get systemUserWieDeployerTags() {
    return this.getSystemUserWieTags('wieDeployerTags');
  }

  getFolderWieTags(type) {
    return [...new Set(this.nonDeletedWieFolders.flatMap(folder => folder[type]))].toSorted();
  }

  getUserFolderWieTags(type) {
    return [...new Set(this.userRepo.getWieTags(type)?.concat(this.getFolderWieTags(type)))].toSorted();
  }

  getSystemFolderWieTags(type) {
    return [...new Set(this.systemConfig.getWieTags(type)?.concat(this.getFolderWieTags(type)))].toSorted();
  }

  getSystemUserWieTags(type) {
    return [...new Set(this.systemConfig.getWieTags(type)?.concat(this.userRepo.getWieTags(type)))].toSorted();
  }

  #hasDefaultPermission() {
    return this.currentUser.isAdmin || (this.currentUser.user?.isUser && !this.isApprovalActive);
  }

  getWritableFolders() {
    if (this.#hasDefaultPermission()) {
      return this.nonDeletedWieFolders;
    }

    return this.nonDeletedWieFolders.filter((folder) => {
      return folder.wieEditorTags?.some((tag) => this.currentUser.user?.wieEditorTags?.includes(tag));
    });
  }

  userCanEdit(instructionOrFolder) {
    if (this.#hasDefaultPermission()) {
      return true;
    }

    return instructionOrFolder?.wieEditorTags?.some((tag) =>
      this.currentUser.user?.wieEditorTags?.includes(tag),
    );
  }

  userCanEditSelected(instructionsOrFolders) {
    if (this.#hasDefaultPermission()) {
      return true;
    }

    return instructionsOrFolders.every((selected) => {
      return this.userCanEdit(selected);
    });
  }

  userCanCreate(folder) {
    if (this.#hasDefaultPermission()) {
      return true;
    }

    if (!folder) {
      return this.userHasRootFolderEditor();
    } else {
      return this.userCanEdit(folder);
    }
  }

  userHasRootFolderEditor() {
    if (this.#hasDefaultPermission()) {
      return true;
    }

    return this.systemConfig
      .getWieTags('wieEditorTags')
      ?.some((tag) => this.currentUser.user?.wieEditorTags?.includes(tag));
  }

  userIsEditorOnly() {
    const { user } = this.currentUser;

    return !isEmpty(user.wieEditorTags) &&
      isEmpty(user.wieApproverTags) &&
      isEmpty(user.wieDeployerTags) &&
      user.isNotAdmin;
  }

  userIsApproverDeployerOnly() {
    const { user } = this.currentUser;

    return isEmpty(user?.wieEditorTags) &&
      (!isEmpty(user?.wieApproverTags) || !isEmpty(user?.wieDeployerTags)) &&
      user.isNotAdmin;
  }

  userCanApprove(workInstruction) {
    if (this.currentUser.isAdmin) {
      return true;
    }

    return workInstruction?.wieApproverTags?.some((tag) =>
      this.currentUser.user.wieApproverTags?.includes(tag),
    );
  }

  userCanApproveScope(scope) {
    if (this.currentUser.isAdmin) {
      return true;
    }

    return Array.isArray(scope)
      ? scope.every(workInstruction => this.userCanApprove(workInstruction))
      : this.userCanApprove(scope);
  }

  userCanDeploy(workInstruction) {
    if (this.currentUser.isAdmin) {
      return true;
    }

    return workInstruction?.wieDeployerTags?.some((tag) =>
      this.currentUser.user.wieDeployerTags?.includes(tag),
    );
  }

  userCanDeployScope(scope) {
    if (this.currentUser.isAdmin) {
      return true;
    }

    return Array.isArray(scope)
      ? scope.every(workInstruction => this.userCanDeploy(workInstruction))
      : this.userCanDeploy(scope);
  }

  queryInstructions = task({ enqueue: true }, waitFor(async query => {
    query.folder ??= 'noFolder';
    query.searchTerm ??= null;
    query.perPage = this.perPage;

    const { searchTerm, pageNumber, folder } = query;

    const {
      content: instructions,
    } = await this.store.request(emberDataQuery('work-instruction', query));

    return {
      searchTerm,
      folder,
      pageNumber,
      perPage: this.perPage,
      instructions: [...instructions],
      count: instructions?.meta?.count ?? 0,
    };
  }));

  saveInstructions = task(waitFor(async workInstructions => {
    await all(workInstructions.map(workInstruction => workInstruction.save()));
  }));

  deleteFiles = task(waitFor(async toDelete => {
    await all(toDelete.map(file => file.destroyRecord()));
  }));

  deleteFolders = task(waitFor(async toDelete => {
    toDelete.forEach((folder) => {
      folder.workInstructions.forEach(workInstruction => { workInstruction.unloadRecord(); });
    });

    await all(toDelete.map(folder => folder.destroyRecord()));
  }));

  deleteWorkInstructions = task(waitFor(async toDelete => {
    await all(toDelete.map(workInstruction => workInstruction.destroyRecord()));
  }));

  saveWithApprovals = task(waitFor(async workInstructions => {
    workInstructions.forEach(function (workInstruction) {
      const pending = workInstruction.approvalsRejects
        .filter(approval => approval.state === WieSettings.approvalStates.PENDING);
      removeObjects(workInstruction.approvalsRejects, pending);
    });

    await workInstructions.map(workInstruction => workInstruction.save());
  }));

  saveWithImageData = task(waitFor(async workInstruction => {
    if (workInstruction.displayImageData != null) {
      await this.workInstructionImageRepo.deleteWorkInstructionPreviews.perform(workInstruction);

      const { image, thumb } = await this.workInstructionImageRepo.saveWieImage.perform(
        workInstruction.displayImageData,
        { type: WieSettings.imageTypes.DISPLAY, thumbnail: true },
      );

      Object.assign(workInstruction, {
        displayImageData: null,
        displayImage: image.id,
        displayImageThumb: thumb.id,
      });
    }

    if (this.isApprovalActive) {
      await workInstruction.save();
    } else {
      await this.deploy.perform(workInstruction);
    }
  }));

  toggleRequestApprovalAndSave = task(waitFor(async workInstruction => {
    workInstruction.approvalRequested = !workInstruction.approvalRequested;
    workInstruction.approvalsRejects = [];
    await workInstruction.save();
  }));

  setPendingApprovalAndSave = task(waitFor(async (workInstructions, username) => {
    await all(
      intoArray(
        reject(workInstruction => workInstruction.approvalsRejects.some(approval => approval.username === username)),
        map(workInstruction =>
          this._approveReject.perform(workInstruction, username, WieSettings.approvalStates.PENDING),
        ),
      )(workInstructions),
    );
  }));

  deployMany = task(waitFor(async workInstructions => {
    await all(
      workInstructions.map(workInstruction => this.deploy.perform(workInstruction)),
    );
  }));

  deploy = task(waitFor(async workInstruction => {
    const [duplicate] = await all([
      this.workInstructionImageRepo.duplicateWieImage.perform(workInstruction.displayImage),
      this.workInstructionImageRepo.deleteWorkInstructionDeployedPreviews.perform(workInstruction),
      this._deleteRemovedImages.perform(workInstruction),
      this._deleteRemovedBackgroundImage.perform(workInstruction),
    ]);

    const isDynamic = workInstruction.getDynamicObjectNames(false).length;

    Object.assign(workInstruction, {
      deployedName: workInstruction.name,
      deployedCanvas: { ...workInstruction.canvas },
      deployedImage: duplicate.image.id,
      deployedImageThumb: duplicate.thumb.id,
      approvalRequested: false,
      approvalsRejects: [],
      isDynamic,
    });

    if (this.systemConfig.jem?.changeNotifications) {
      await all([this.logRepo.deployBySource.perform(workInstruction.id), workInstruction.save()]);
    } else {
      await workInstruction.save();
    }
  }));

  approveAndSave = task(waitFor(async workInstructions => {
    await all(
      workInstructions.map(workInstruction => {
        workInstruction.currentApproveReject?.approve();
        return workInstruction.save();
      }),
    );
  }));

  rejectAndSave = task(waitFor(async workInstructions => {
    await all(
      workInstructions.map(workInstruction => {
        workInstruction.currentApproveReject?.reject();
        return workInstruction.save();
      }),
    );
  }));

  _approveReject = task(waitFor(async (workInstruction, username, state, notes = null) => {
    const current = workInstruction.approvalsRejects.filter(approval => approval.username === username);
    removeObjects(workInstruction.approvalsRejects, current);

    const approveReject = this.store.createRecord('work-instruction-approve-reject', {
      username,
      state,
      notes,
    });

    workInstruction.approvalsRejects.push(approveReject);
    await workInstruction.save();
  }));

  _deleteRemovedImages = task(waitFor(async workInstruction => {
    if (
      workInstruction.canvas == null ||
      workInstruction.canvas.objects == null ||
      workInstruction.deployedCanvas == null ||
      workInstruction.deployedCanvas.objects == null
    ) {
      return;
    }

    await all(
      intoArray(
        dpPluck('eflex.id'),
        compact,
        filter((imageId) => !this.getAllCanvasImages(workInstruction.canvas).some(item => item.eflex?.id === imageId)),
        map(imageId => this.workInstructionImageRepo.delete.perform(imageId)),
      )(this.getAllCanvasImages(workInstruction.deployedCanvas)),
    );
  }));

  _deleteRemovedBackgroundImage = task(waitFor(async workInstruction => {
    const { canvas } = workInstruction;
    const { deployedCanvas } = workInstruction;

    if (canvas == null || deployedCanvas?.backgroundImage == null) {
      return;
    }

    if (canvas.backgroundImage?.eflex.isExternal || deployedCanvas.backgroundImage?.eflex.isExternal) {
      return;
    }

    if (canvas.backgroundImage?.eflex.id !== deployedCanvas.backgroundImage.eflex.id) {
      await this.workInstructionImageRepo.delete.perform(deployedCanvas.backgroundImage.eflex.id);
    }
  }));

  deleteSelectedLibraryItems = task(waitFor(async (currentWorkInstruction, libraryItems) => {
    const files = libraryItems.filter(item => item.isFile);
    const folders = libraryItems.filter(item => item.isFolder);
    const workInstructions = libraryItems.filter(item => item.isInstruction);

    const currentWorkInstructionIsDeleted =
      currentWorkInstruction != null &&
      (workInstructions.includes(currentWorkInstruction) ||
      (currentWorkInstruction.folder != null && folders.includes(currentWorkInstruction.folder)));

    await this.deleteFiles.perform(files);
    await this.deleteFolders.perform(folders);
    await this.deleteWorkInstructions.perform(workInstructions);

    return currentWorkInstructionIsDeleted;
  }));


  downloadInstructions = task(waitFor(async (downloadQueue, fileType) => {
    const ids = downloadQueue.filter(item => !item.isFolder).map(item => item.id);
    const folderIds = downloadQueue.filter(item => item.isFolder).map(item => item.id);

    if (isEmpty(ids) && isEmpty(folderIds)) {
      return;
    }

    if (ids.length === 1 && folderIds.length === 0) {
      await this._downloadSingle.perform(downloadQueue[0], fileType);
    } else {
      await this._downloadMultiple.perform(fileType, ids, folderIds);
    }
  }));

  _downloadSingle = task({ drop: true }, waitFor(async (instruction, fileType) => {
    const fileTypeSuffix = fileType.split('/')[1];
    await this.fileDownloader.getFile.perform(
      `workInstructionImages/${instruction.displayImage}`,
      fileType,
      `work-instructions_${dasherize(instruction.name)}.${fileTypeSuffix}`,
    );
  }));

  _downloadMultiple = task({ drop: true }, waitFor(async (fileType, ids, folderIds) => {
    let queryString = '';

    ids.forEach(id => {
      queryString += `ids[]=${id}&`;
    });

    folderIds.forEach(id => {
      queryString += `folderIds[]=${id}&`;
    });

    await this.fileDownloader.getFile.perform(
      'workInstructions',
      `${fileType}, application/zip`,
      `work-instructions_${Date.now()}.zip`,
      queryString,
    );
  }));

  createWorkInstruction({ width, height, name, notes, canvas, displayImageData, folder }) {
    const props = {
      authorUsername: this.currentUser.user?.userName,
      width,
      height,
      notes,
    };

    if (name != null) {
      props.name = name;
    }
    if (canvas != null) {
      props.canvas = canvas;
    }
    if (displayImageData != null) {
      props.displayImageData = displayImageData;
    }
    if (folder != null) {
      props.folder = folder;
    }

    return this.store.createRecord('work-instruction', props);
  }

  getAllCanvasImages(source) {
    const images = source.objects.filter(item => item.type.toLowerCase() === 'image');
    source.objects
      .filter(item => item.type.toLowerCase() === 'group')
      .forEach((group) => {
        images.push(...this.getAllCanvasImages(group));
      });

    return images;
  }

  createFolder() {
    return this.store.createRecord('work-instruction-folder', {});
  }

  setExternalImageUrl(jemConfig, url, page = null) {
    Object.assign(jemConfig, {
      externalImageUrl: url,
      workInstruction: null,
      page,
    });

    if (this.animations != null) {
      jemConfig.animations = [];
    }
  }

  setWorkInstruction(jemConfig, workInstruction) {
    Object.assign(jemConfig, {
      externalImageUrl: null,
      workInstruction,
      page: null,
    });

    if (jemConfig.animations != null) {
      jemConfig.animations = [];
    }
  }

  searchFolders(searchTerm) {
    let folders = this.folders;

    if (!isBlank(searchTerm)) {
      searchTerm = searchTerm.toLowerCase();
      folders = folders.filter((folder) => folder.name?.toLowerCase().includes(searchTerm));
    }

    return folders;
  }
}
